import React, { useEffect, useState } from "react";
import { Badge, Checkbox, Radio, RadioGroup, Tag, TextInput, Toggle } from "@opendate-app/setlist";

import BlurTextInput from "../BlurTextInput";
import { useLandingPage } from "../LandingPageBuilderContainer";
import CountBadge from "../../../shared/components/CountBadge";

const styles = {
  stack: { display: "flex", flexDirection: "column", gap: 28, marginTop: 24 },
  section: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--sl-text-secondary)" },
  hint: { fontSize: 13, color: "var(--sl-text-tertiary)" },
  segmentRow: { display: "flex", alignItems: "center", gap: 8 },
  chips: { display: "flex", flexWrap: "wrap", gap: 8 },
  soon: { display: "flex", flexDirection: "column", gap: 4 },
  soonHead: { display: "flex", alignItems: "center", gap: 8 },
};

const ComingSoonToggle = ({ label, hint }) => (
  <div style={styles.soon}>
    <div style={styles.soonHead}>
      <Toggle label={label} checked={false} disabled />
      <Badge size="sm" color="gray">Coming soon</Badge>
    </div>
    <div style={styles.hint}>{hint}</div>
  </div>
);

const SignupsStep = () => {
  const { page, venueData, savePage, setPreviewView } = useLandingPage();
  const [newTag, setNewTag] = useState("");
  const settings = page.settings || {};
  const segmentIds = settings.segment_ids || [];
  const tagNames = settings.tag_names || [];
  const after = settings.after || "thanks";
  const allTags = [...venueData.tags, ...tagNames.filter((name) => !venueData.tags.includes(name))];

  useEffect(() => () => setPreviewView("page"), []);

  const saveSetting = (key) => (value) => savePage({ settings: { [key]: value } });

  const toggleSegment = (id, checked) => {
    saveSetting("segment_ids")(checked ? [...segmentIds, id] : segmentIds.filter((existing) => existing !== id));
  };

  const toggleTag = (name, selected) => {
    saveSetting("tag_names")(selected ? [...tagNames, name] : tagNames.filter((existing) => existing !== name));
  };

  const addTag = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const name = newTag.trim();
    if (name && !tagNames.includes(name)) saveSetting("tag_names")([...tagNames, name]);
    setNewTag("");
  };

  return (
    <div style={styles.stack}>
      <div style={styles.section}>
        <div style={styles.label}>Add them to a segment</div>
        <Checkbox
          label="New segment: signups from this page"
          checked={Boolean(settings.create_segment)}
          onChange={(event) => saveSetting("create_segment")(event.target.checked)}
        />
        {venueData.segments.map((segment) => (
          <div key={segment.id} style={styles.segmentRow}>
            <Checkbox
              label={segment.name}
              checked={segmentIds.includes(segment.id)}
              onChange={(event) => toggleSegment(segment.id, event.target.checked)}
            />
            <CountBadge count={segment.count} color="gray" />
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <div style={styles.label}>Tag them</div>
        <div style={styles.chips}>
          {allTags.map((name) => (
            <Tag key={name} selectable selected={tagNames.includes(name)} onSelectedChange={(selected) => toggleTag(name, selected)}>
              {name}
            </Tag>
          ))}
        </div>
        <TextInput
          aria-label="Add a tag"
          placeholder="Add a tag and press Enter"
          value={newTag}
          onChange={(event) => setNewTag(event.target.value)}
          onKeyDown={addTag}
        />
      </div>

      <ComingSoonToggle label="Send a welcome email" hint="Goes out the moment someone signs up." />
      <ComingSoonToggle label="Notify me" hint="Get a daily email with new signups." />

      <div style={styles.section}>
        <div style={styles.label}>After they submit</div>
        <RadioGroup name="landing-page-after" value={after} onChange={saveSetting("after")}>
          <Radio value="thanks" label="Show thank-you page" />
          <Radio value="redirect" label="Send to a website" />
        </RadioGroup>
        {after === "redirect" ? (
          <BlurTextInput
            aria-label="Redirect URL"
            placeholder="https://"
            value={settings.redirect_url}
            onFocus={() => setPreviewView("thanks")}
            onSave={saveSetting("redirect_url")}
          />
        ) : (
          <BlurTextInput
            aria-label="Thank-you message"
            multiline
            rows={3}
            value={settings.thanks_message}
            onFocus={() => setPreviewView("thanks")}
            onSave={saveSetting("thanks_message")}
          />
        )}
      </div>
    </div>
  );
};

export default SignupsStep;
